import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader';
import './MeshPreview.css';
import { apiClient } from '../../services/authService';
import { debugLog, warnLog, errorLog } from '../../utils/logger';

const isWebGLAvailable = () => {
  try {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    return Boolean(window.WebGLRenderingContext) && Boolean(context);
  } catch (error) {
    return false;
  }
};

const normalizePath = (path = '') => {
  const normalized = `/${String(path || '').replace(/^\/+/, '')}`;
  return normalized.replace(/\/+$/, '') || '/';
};

const shouldUseApiClient = (url) => {
  if (!url || typeof url !== 'string') {
    return false;
  }

  if (!/^https?:\/\//i.test(url)) {
    return true;
  }

  try {
    const requestUrl = new URL(url);
    const base = new URL(
      apiClient.defaults.baseURL || '/api/v1',
      typeof window !== 'undefined' ? window.location.origin : 'http://localhost'
    );
    const apiPath = normalizePath(base.pathname);
    const requestPath = normalizePath(requestUrl.pathname);
    const isApiOrigin = requestUrl.origin === base.origin;
    const isApiPath = requestPath === apiPath || requestPath.startsWith(`${apiPath}/`);
    return isApiOrigin && isApiPath;
  } catch (_error) {
    return false;
  }
};

const fetchWithAuth = async (url, type = 'blob') => {
  const responseType = type === 'text' ? 'text' : 'blob';

  if (shouldUseApiClient(url)) {
    const response = await apiClient.get(url, {
      responseType,
      headers: { Accept: type === 'text' ? 'text/plain' : 'application/octet-stream' },
    });
    return response.data;
  }

  const response = await fetch(url, {
    method: 'GET',
    credentials: 'omit',
    headers: { Accept: type === 'text' ? 'text/plain' : 'application/octet-stream' },
  });
  if (!response.ok) {
    throw new Error(`Asset fetch failed with status ${response.status}`);
  }
  if (type === 'text') {
    return response.text();
  }
  return response.blob();
};

const decimateMesh = (object, originalVertexCount) => {
  debugLog('Starting mesh decimation...');
  
  // Target vertex count based on original size
  let targetVertices;
  if (originalVertexCount > 500000) {
    targetVertices = 50000; // Aggressive decimation for very large meshes
  } else if (originalVertexCount > 200000) {
    targetVertices = 75000; // Moderate decimation
  } else {
    targetVertices = 100000; // Light decimation
  }
  
  const decimationRatio = Math.min(targetVertices / originalVertexCount, 1.0);
  debugLog(`Decimation ratio: ${decimationRatio.toFixed(3)} (target: ${targetVertices} vertices)`);
  
  object.traverse((child) => {
    if (child.isMesh && child.geometry) {
      try {
        child.geometry = decimateGeometry(child.geometry, decimationRatio);
      } catch (error) {
        warnLog('Failed to decimate mesh, using original:', error);
      }
    }
  });
  
  return object;
};

const decimateGeometry = (geometry, ratio) => {
  // Simple vertex decimation by sampling (only for indexed geometry)
  const positions = geometry.attributes.position;
  const normals = geometry.attributes.normal;
  const uvs = geometry.attributes.uv;
  const indices = geometry.index;
  
  if (!positions) {
    return geometry;
  }
  // Avoid decimation for non-indexed geometries to prevent broken faces
  if (!indices) {
    warnLog('Skipping decimation: geometry has no index');
    return geometry;
  }
  
  const vertexCount = positions.count;
  const targetCount = Math.floor(vertexCount * ratio);
  
  // For very high decimation ratios, decimation of non-indexed geom is unsafe (handled above)
  if (ratio < 0.5) {
    return uniformSampleGeometry(geometry, targetCount);
  }
  
  // For moderate decimation, use every nth vertex
  const step = Math.ceil(1 / ratio);
  const newPositions = [];
  const newNormals = [];
  const newUvs = [];
  const newIndices = [];
  
  const vertexMap = new Map();
  let newVertexIndex = 0;
  
  // Sample vertices
  for (let i = 0; i < vertexCount; i += step) {
    if (newVertexIndex >= targetCount) break;
    
    // Position
    newPositions.push(
      positions.getX(i),
      positions.getY(i),
      positions.getZ(i)
    );
    
    // Normal
    if (normals) {
      newNormals.push(
        normals.getX(i),
        normals.getY(i),
        normals.getZ(i)
      );
    }
    
    // UV
    if (uvs) {
      newUvs.push(
        uvs.getX(i),
        uvs.getY(i)
      );
    }
    
    vertexMap.set(i, newVertexIndex);
    newVertexIndex++;
  }
  
  // Rebuild faces if we have indices
  if (indices) {
    for (let i = 0; i < indices.count; i += 3) {
      const a = indices.getX(i);
      const b = indices.getX(i + 1);
      const c = indices.getX(i + 2);
      
      const newA = vertexMap.get(Math.floor(a / step) * step);
      const newB = vertexMap.get(Math.floor(b / step) * step);
      const newC = vertexMap.get(Math.floor(c / step) * step);
      
      if (newA !== undefined && newB !== undefined && newC !== undefined && 
          newA !== newB && newB !== newC && newA !== newC) {
        newIndices.push(newA, newB, newC);
      }
    }
  }
  
  // Create new geometry
  const newGeometry = new THREE.BufferGeometry();
  newGeometry.setAttribute('position', new THREE.Float32BufferAttribute(newPositions, 3));
  
  if (newNormals.length > 0) {
    newGeometry.setAttribute('normal', new THREE.Float32BufferAttribute(newNormals, 3));
  }
  
  if (newUvs.length > 0) {
    newGeometry.setAttribute('uv', new THREE.Float32BufferAttribute(newUvs, 2));
  }
  
  if (newIndices.length > 0) {
    newGeometry.setIndex(newIndices);
  }
  
  // Compute normals if not present
  if (!normals || newNormals.length === 0) {
    newGeometry.computeVertexNormals();
  }
  
  debugLog(`Geometry decimated: ${vertexCount} → ${newVertexIndex} vertices`);
  return newGeometry;
};

const uniformSampleGeometry = (geometry, targetCount) => {
  const positions = geometry.attributes.position;
  const normals = geometry.attributes.normal;
  const uvs = geometry.attributes.uv;
  
  const vertexCount = positions.count;
  const step = Math.max(1, Math.floor(vertexCount / targetCount));
  
  const newPositions = [];
  const newNormals = [];
  const newUvs = [];
  
  for (let i = 0; i < vertexCount && newPositions.length / 3 < targetCount; i += step) {
    newPositions.push(
      positions.getX(i),
      positions.getY(i),
      positions.getZ(i)
    );
    
    if (normals) {
      newNormals.push(
        normals.getX(i),
        normals.getY(i),
        normals.getZ(i)
      );
    }
    
    if (uvs) {
      newUvs.push(
        uvs.getX(i),
        uvs.getY(i)
      );
    }
  }
  
  const newGeometry = new THREE.BufferGeometry();
  newGeometry.setAttribute('position', new THREE.Float32BufferAttribute(newPositions, 3));
  
  if (newNormals.length > 0) {
    newGeometry.setAttribute('normal', new THREE.Float32BufferAttribute(newNormals, 3));
  } else {
    newGeometry.computeVertexNormals();
  }
  
  if (newUvs.length > 0) {
    newGeometry.setAttribute('uv', new THREE.Float32BufferAttribute(newUvs, 2));
  }
  
  debugLog(`Uniform sampling: ${vertexCount} → ${newPositions.length / 3} vertices`);
  return newGeometry;
};

const MeshPreview = ({ objUrl, textureUrl, className = '' }) => {
  const containerRef = useRef(null);
  const animationRef = useRef(null);
  const rendererRef = useRef(null);
  const [loading, setLoading] = useState(Boolean(objUrl));
  const [loadError, setLoadError] = useState('');
  const [loadingStage, setLoadingStage] = useState('');

  const webglSupported = useMemo(() => isWebGLAvailable(), []);

  useEffect(() => {
    if (!objUrl || !containerRef.current || !webglSupported) {
      debugLog('MeshPreview: Skipping load due to missing requirements:', {
        objUrl: !!objUrl,
        container: !!containerRef.current,
        webglSupported
      });
      return undefined;
    }

    let isCancelled = false;
    let textureObjectUrl = null;
    let meshTexture = null;
    setLoading(true);
    setLoadError('');
    setLoadingStage('Downloading mesh file...');
    
    debugLog('MeshPreview: Starting load process for:', objUrl);

    const container = containerRef.current;
    const containerWidth = container.clientWidth || 540;
    const containerHeight = container.clientHeight || 380;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xf5f5f5);

    const camera = new THREE.PerspectiveCamera(45, containerWidth / containerHeight, 0.1, 1000);
    camera.position.set(1.5, 1.2, 2.2);

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setPixelRatio(window.devicePixelRatio || 1);
    renderer.setSize(containerWidth, containerHeight);
    container.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;
    controls.target.set(0, 0, 0);

    const ambientLight = new THREE.AmbientLight(0xffffff, 0.7);
    scene.add(ambientLight);
    const mainLight = new THREE.DirectionalLight(0xffffff, 0.85);
    mainLight.position.set(3, 4, 5);
    scene.add(mainLight);
    const backLight = new THREE.DirectionalLight(0xffffff, 0.4);
    backLight.position.set(-3, -2, -4);
    scene.add(backLight);

    const debugVisuals = process.env.REACT_APP_ENABLE_CLIENT_LOGS === 'true';

    const applyMaterial = (object, loadedTexture) => {
      object.traverse((child) => {
        if (child.isMesh) {
          if (loadedTexture) {
            child.material = new THREE.MeshStandardMaterial({
              map: loadedTexture,
              metalness: 0.1,
              roughness: 0.85,
            });
          } else {
            child.material = new THREE.MeshStandardMaterial({ color: 0xd1d1d1, metalness: 0.05, roughness: 0.85 });
          }
          child.castShadow = true;
          child.receiveShadow = true;
        }
      });
    };

    const centerObject = (object) => {
      // Compute initial bounds
      object.updateMatrixWorld(true);
      let boundingBox = new THREE.Box3().setFromObject(object);
      let center = boundingBox.getCenter(new THREE.Vector3());
      let size = boundingBox.getSize(new THREE.Vector3());

      debugLog('Object bounding box (initial):', {
        center: { x: center.x, y: center.y, z: center.z },
        size: { x: size.x, y: size.y, z: size.z },
        isEmpty: boundingBox.isEmpty()
      });

      // Normalize extreme scales for better camera precision
      const maxDimInit = Math.max(size.x, size.y, size.z) || 1;
      let scaleFactor = 1;
      // If the object is extremely large or small, rescale to a reasonable size (~2 units)
      if (maxDimInit > 1000) {
        scaleFactor = 2 / maxDimInit;
      } else if (maxDimInit < 0.01) {
        scaleFactor = 2 / (maxDimInit || 1);
      }

      if (scaleFactor !== 1) {
        debugLog('Rescaling object for preview:', { scaleFactor });
        object.scale.multiplyScalar(scaleFactor);
        object.updateMatrixWorld(true);
        boundingBox = new THREE.Box3().setFromObject(object);
        center = boundingBox.getCenter(new THREE.Vector3());
        size = boundingBox.getSize(new THREE.Vector3());
      }

      // Center the object at world origin
      object.position.sub(center);
      object.updateMatrixWorld(true);

      const maxDim = Math.max(size.x, size.y, size.z) || 1;
      const optimalDistance = maxDim * 1.8;

      // Adapt near/far planes to object size for visibility
      const newNear = Math.max(0.001, maxDim / 1000);
      const newFar = Math.max(50, maxDim * 100);
      camera.near = newNear;
      camera.far = newFar;
      camera.updateProjectionMatrix();

      debugLog('Camera positioning:', {
        maxDimension: maxDim,
        optimalDistance,
        near: newNear,
        far: newFar,
        newCameraPosition: {
          x: optimalDistance,
          y: optimalDistance * 0.7,
          z: optimalDistance
        }
      });

      camera.position.set(optimalDistance, optimalDistance * 0.7, optimalDistance);
      camera.lookAt(0, 0, 0);
      controls.target.set(0, 0, 0);
      controls.update();
    };

    const handleLoadSuccess = (object) => {
      if (isCancelled) {
        return;
      }
      debugLog('Handling load success...');
      
      object.traverse((child) => {
        if (child.isMesh && child.material) {
          child.material.side = THREE.DoubleSide;
          debugLog('Set double-sided material for mesh:', child.name);
        }
      });

      // Material has already been applied earlier based on available texture.
      // Avoid overriding it here to preserve texture rendering.
      
      debugLog('Centering object...');
      centerObject(object);
      
      debugLog('Adding object to scene...');
      scene.add(object);
      
      // Optional debug helpers
      if (debugVisuals) {
        try {
          const bbox = new THREE.Box3().setFromObject(object);
          const bboxHelper = new THREE.Box3Helper(bbox, 0x00ff00);
          scene.add(bboxHelper);
          const axes = new THREE.AxesHelper(Math.max(1, bbox.getSize(new THREE.Vector3()).length()));
          scene.add(axes);
          debugLog('Added debug helpers (Box3Helper, AxesHelper)');
        } catch (e) {
          warnLog('Failed to add debug helpers:', e);
        }
      }
      
      debugLog('Mesh preview loaded successfully!');
      setLoading(false);
      setLoadingStage('');
    };

    const handleLoadError = (error) => {
      if (isCancelled) {
        return;
      }
      errorLog('Mesh loading error:', error);
      errorLog('Error details:', {
        message: error?.message,
        stack: error?.stack,
        name: error?.name,
        objUrl,
        textureUrl
      });
      
      // Provide more specific error messages
      let errorMessage = 'Unable to load mesh preview.';
      if (error?.message?.includes('NetworkError') || error?.message?.includes('fetch')) {
        errorMessage = 'Network error loading mesh. Please check your connection.';
      } else if (error?.message?.includes('parse') || error?.message?.includes('format')) {
        errorMessage = 'Invalid mesh file format. The file may be corrupted.';
      } else if (error?.message?.includes('memory') || error?.message?.includes('size')) {
        errorMessage = 'Mesh file too large for browser memory. Try a smaller file.';
      }
      
      setLoadError(errorMessage);
      setLoading(false);
    };

    const objLoader = new OBJLoader();

    // Add timeout for large files
    let loadTimeout = null;
    
    const loadMesh = async () => {
      try {
        debugLog('Starting mesh load process...', { objUrl, textureUrl });
        
        loadTimeout = setTimeout(() => {
          if (!isCancelled) {
            warnLog('Mesh loading timeout - file may be too large');
            handleLoadError(new Error('Loading timeout - file may be too large for browser memory'));
          }
        }, 30000); // 30 second timeout
        
        let appliedTexture = null;

        if (textureUrl) {
          try {
            debugLog('Loading texture from:', textureUrl);
            const textureBlob = await fetchWithAuth(textureUrl, 'blob');
            if (isCancelled) {
              return;
            }
            debugLog('Texture blob loaded, size:', textureBlob.size);
            textureObjectUrl = URL.createObjectURL(textureBlob);
            const loader = new THREE.TextureLoader();
            appliedTexture = await new Promise((resolve, reject) => {
              loader.load(
                textureObjectUrl,
                (texture) => {
                  debugLog('Texture loaded successfully');
                  resolve(texture);
                },
                undefined,
                (textureError) => {
                  errorLog('Texture loading error:', textureError);
                  reject(textureError);
                }
              );
            });
          } catch (textureError) {
            errorLog('Texture fetch error:', textureError);
          }
        }

        debugLog('Loading OBJ file from:', objUrl);
        let objText = await fetchWithAuth(objUrl, 'text');
        // In some environments, axios may still return a Blob; normalize to string
        if (objText && typeof objText !== 'string' && objText instanceof Blob) {
          debugLog('OBJ response was Blob, converting to text...');
          objText = await objText.text();
        }
        if (isCancelled) {
          if (appliedTexture) {
            appliedTexture.dispose();
          }
          return;
        }

        debugLog('OBJ text loaded, length:', objText.length, 'characters');
        
        // Check if file is very large
        const fileSizeMB = objText.length / (1024 * 1024);
        debugLog('OBJ file size:', fileSizeMB.toFixed(2), 'MB');
        
        if (fileSizeMB > 10) {
          debugLog('Large OBJ file detected, will apply optimization for browser display');
          setLoadingStage('Optimizing large mesh for browser...');
        } else {
          setLoadingStage('Parsing 3D geometry...');
        }
        
        debugLog('Parsing OBJ with Three.js...');
        
        // Use try-catch specifically for parsing to catch memory errors
        let object;
        try {
          object = objLoader.parse(objText);
        } catch (parseError) {
          errorLog('OBJ parsing failed:', parseError);
          if (parseError.message.includes('memory') || fileSizeMB > 100) {
            throw new Error('Mesh file too large for browser memory. Try a smaller file.');
          }
          throw parseError;
        }
        debugLog('OBJ parsed successfully, object:', object);
        debugLog('Object children count:', object.children.length);
        
        // Check if object has any meshes
        let meshCount = 0;
        let totalVertices = 0;
        
        object.traverse((child) => {
          if (child.isMesh) {
            meshCount++;
            const vertexCount = child.geometry?.attributes?.position?.count || 0;
            totalVertices += vertexCount;
            debugLog('Mesh found:', {
              name: child.name,
              geometry: child.geometry,
              vertices: vertexCount,
              faces: child.geometry?.index ? child.geometry.index.count / 3 : 'No index',
              boundingBox: child.geometry?.boundingBox
            });
          }
        });
        
        debugLog('Total meshes:', meshCount, 'Total vertices:', totalVertices);
        
        if (meshCount === 0) {
          throw new Error('No valid mesh geometry found in OBJ file');
        }
        
        // Apply mesh optimization for high vertex counts
        if (totalVertices > 100000) {
          setLoadingStage(`Optimizing mesh (${totalVertices.toLocaleString()} vertices)...`);
          debugLog(`High vertex count detected (${totalVertices.toLocaleString()}), applying mesh decimation...`);
          object = decimateMesh(object, totalVertices);
          
          // Recalculate vertex count after decimation
          totalVertices = 0;
          object.traverse((child) => {
            if (child.isMesh) {
              totalVertices += child.geometry?.attributes?.position?.count || 0;
            }
          });
          debugLog(`Mesh decimated to ${totalVertices.toLocaleString()} vertices`);
        }
        
        setLoadingStage('Preparing 3D scene...');
        
        if (loadTimeout) {
          clearTimeout(loadTimeout);
          loadTimeout = null;
        }
        meshTexture = appliedTexture;
        applyMaterial(object, appliedTexture);
        handleLoadSuccess(object);
      } catch (error) {
        if (loadTimeout) {
          clearTimeout(loadTimeout);
          loadTimeout = null;
        }
        handleLoadError(error);
      }
    };

    loadMesh();

    const handleResize = () => {
      if (!rendererRef.current || !containerRef.current) {
        return;
      }
      const { clientWidth, clientHeight } = containerRef.current;
      camera.aspect = (clientWidth || containerWidth) / (clientHeight || containerHeight);
      camera.updateProjectionMatrix();
      renderer.setSize(clientWidth || containerWidth, clientHeight || containerHeight);
    };

    window.addEventListener('resize', handleResize);

    const renderScene = () => {
      controls.update();
      renderer.render(scene, camera);
      animationRef.current = requestAnimationFrame(renderScene);
    };

    debugLog('Starting render loop...');
    renderScene();

    return () => {
      isCancelled = true;
      if (loadTimeout) {
        clearTimeout(loadTimeout);
        loadTimeout = null;
      }
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      window.removeEventListener('resize', handleResize);
      controls.dispose();

      scene.traverse((child) => {
        if (child.isMesh) {
          if (child.geometry) {
            child.geometry.dispose();
          }
          const material = child.material;
          if (Array.isArray(material)) {
            material.forEach((mat) => {
              if (mat?.map) {
                mat.map.dispose();
              }
              if (mat?.dispose) {
                mat.dispose();
              }
            });
          } else if (material) {
            if (material.map) {
              material.map.dispose();
            }
            if (material.dispose) {
              material.dispose();
            }
          }
        }
      });

      renderer.dispose();
      if (renderer.domElement.parentNode) {
        renderer.domElement.parentNode.removeChild(renderer.domElement);
      }

      if (meshTexture) {
        meshTexture.dispose();
      }

      if (textureObjectUrl) {
        URL.revokeObjectURL(textureObjectUrl);
      }
    };
  }, [objUrl, textureUrl, webglSupported]);

  if (!objUrl) {
    return <div className={`mesh-preview ${className}`}>Mesh output is not available.</div>;
  }

  if (!webglSupported) {
    return <div className={`mesh-preview mesh-preview--unsupported ${className}`}>WebGL is not available in this browser.</div>;
  }

  return (
    <div className={`mesh-preview ${className}`}>
      <div ref={containerRef} className="mesh-preview__canvas" />
      {loading && !loadError ? (
        <div className="mesh-preview__overlay">
          <div className="mesh-preview__loading">
            <div className="mesh-preview__spinner"></div>
            <p>{loadingStage || 'Loading 3D preview…'}</p>
          </div>
        </div>
      ) : null}
      {loadError ? <div className="mesh-preview__overlay mesh-preview__overlay--error">{loadError}</div> : null}
    </div>
  );
};

export default MeshPreview;
